import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppState } from '@api-ui-app/src/app/app.state';
import {
  addProductTemplateToStore,
  createProductTemplate,
  deleteProductTemplate,
  duplicateProductTemplate,
  editProductTemplate,
  loadMore,
  loadStart,
  logProductTemplateUsageInitiatedEvent,
  refresh,
  renameProductTemplate,
  setLoadingState,
  setSearchParams,
  setSortingParams,
  setTemplateName,
} from '@api-ui-app/src/app/e-commerce-product-templates/+state/e-commerce-product-templates.actions';
import {
  getIsLoading,
  getIsProcessing,
  getItems,
  getItemsCount,
  getProductTemplateUsageInitiatedAt,
  getProductTemplateUsageStep,
  getSearchParams,
  getSortingParams,
  getTemplateName,
} from '@api-ui-app/src/app/e-commerce-product-templates/+state/e-commerce-product-templates.selectors';
import { ProductTemplatesStoreSelectionModalComponent } from '@api-ui-app/src/app/e-commerce-product-templates/containers/product-templates-store-selection-modal/product-templates-store-selection-modal.component';
import { ProductTemplatesVariantSelectionModalComponent } from '@api-ui-app/src/app/e-commerce-product-templates/containers/product-templates-variant-selection-modal/product-templates-variant-selection-modal.component';
import { EProductTemplateUsageEventStep } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-template-usage-event-step.enum';
import { EProductTemplatesListItem } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-templates-list-item';
import { EProductTemplatesSearchParams } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-templates-search-params';
import { EProductTemplatesSearchSortingParams } from '@api-ui-app/src/app/e-commerce-product-templates/types/e-product-templates-search-sorting-params';
import { getSelectedClientId } from '@api-ui-app/src/app/ngrx/auth.reducer';
import { LoadStoresList } from '@api-ui-app/src/app/ngrx/e-commerce-stores.actions';
import {
  getSelectedStoreId,
  getStoreType,
  getStoresList,
  getStoresListLoadingFlag,
} from '@api-ui-app/src/app/ngrx/e-commerce-stores.selector';
import { TitleService } from '@api-ui-app/src/app/shared/services/title.service';
import { AuthService } from '@gelato-api-ui/core/auth/auth.service';
import { EProductVariant } from '@gelato-api-ui/core/e-commerce/e-product-variant';
import { EStore } from '@gelato-api-ui/core/e-commerce/e-store';
import { getDefaultStoresListRequest } from '@gelato-api-ui/core/e-commerce/helpers/getDefaultStoresListRequest';
import { ECommerceProductGraphQlService } from '@gelato-api-ui/core/e-commerce/services/e-commerce-product-graph-ql.service';
import { DialogService } from '@gelato-api-ui/sdk/src/lib/shared/dialog/dialog.service';
import { ConfirmModalService } from '@gelato-api-ui/sdk/src/lib/shared/services/confirm-modal.service';
import { ECommerceProductTemplateNameModalService } from '@gelato-api-ui/sdk/src/lib/shared/services/e-commerce-product-template-name-modal.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { StoreProductMode } from '@product-catalogue/src/lib/product-catalogue/lib/store-product-mode.enum';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable()
export class ECommerceProductTemplatesFacade {
  clientId$ = this.store.select(getSelectedClientId);
  selectedStoreId$ = this.store.select(getSelectedStoreId);
  isLoading$ = this.store.select(getIsLoading);
  isProcessing$ = this.store.select(getIsProcessing);
  searchParams$ = this.store.select(getSearchParams);
  templateName$ = this.store.select(getTemplateName);
  sortingParams$ = this.store.select(getSortingParams);
  items$ = this.store.select(getItems);
  itemsCount$ = this.store.select(getItemsCount);
  productTemplateUsageStep$ = this.store.select(getProductTemplateUsageStep);
  storeType$ = this.store.select(getStoreType);
  productTemplateUsageInitiatedAt$ = this.store.select(getProductTemplateUsageInitiatedAt);

  storesList$ = this.store.select(getStoresList);
  isStoresListLoading$ = this.store.select(getStoresListLoadingFlag);
  isGelatoUser$ = this.authService.isGelatoUser();

  constructor(
    private readonly router: Router,
    private readonly store: Store<AppState>,
    private readonly translateService: TranslateService,
    private readonly dialogService: DialogService,
    private readonly confirmModalService: ConfirmModalService,
    private readonly eCommerceProductTemplateNameModalService: ECommerceProductTemplateNameModalService,
    private readonly titleService: TitleService,
    private readonly authService: AuthService,
    private readonly eCommerceProductGraphQlService: ECommerceProductGraphQlService,
  ) {}

  setPageTitle() {
    this.titleService.setTitle('{txt_meta_title_templates}');
  }

  load() {
    this.store.dispatch(loadStart());
  }

  loadMore() {
    this.store.dispatch(loadMore());
  }

  refresh() {
    this.store.dispatch(refresh());
  }

  deleteProductTemplate(item: EProductTemplatesListItem) {
    this.confirmModalService
      .open({
        title: this.translateService.instant('txt_warning'),
        titleIconUrl: '/assets/icons/warning-black.svg',
        body: this.translateService.instant('txt_template_deletion_confirmation_modal_text'),
        approveButtonStyle: 'negative',
        approveButtonText: this.translateService.instant('txt_delete_template'),
        denyButtonText: this.translateService.instant('txt_cancel'),
      })
      .onApprove(() => {
        this.store.dispatch(deleteProductTemplate({ id: item?.id }));
      });
  }

  duplicateProductTemplate(item: EProductTemplatesListItem) {
    this.eCommerceProductTemplateNameModalService
      .openTemplateCreationModal(item?.name)
      .afterClosed()
      .pipe(
        take(1),
        filter((templateName: string) => !!templateName),
        map((templateName: string) => {
          this.store.dispatch(duplicateProductTemplate({ id: item?.id, templateName }));
        }),
      )
      .subscribe();
  }

  renameProductTemplate(item: EProductTemplatesListItem) {
    this.eCommerceProductTemplateNameModalService
      .openTemplateRenamingModal(item?.name)
      .afterClosed()
      .pipe(
        take(1),
        filter((templateName: string) => !!templateName),
        map((templateName: string) => {
          this.store.dispatch(renameProductTemplate({ id: item?.id, templateName }));
        }),
      )
      .subscribe();
  }

  createNewTemplate() {
    this.store.dispatch(createProductTemplate());
  }

  editTemplate(id: string) {
    this.store.dispatch(editProductTemplate({ id }));
  }

  loadStoresList() {
    this.store.dispatch(new LoadStoresList(getDefaultStoresListRequest()));
  }

  addTemplateToStore(item: EProductTemplatesListItem) {
    this.logProductTemplateUsageInitiatedEvent(EProductTemplateUsageEventStep.ADD_TO_STORE);

    this.dialogService
      .open(ProductTemplatesStoreSelectionModalComponent, {
        data: {
          templateName: item?.name,
        },
      })
      .afterClosed()
      .pipe(
        take(1),
        filter(result => !!result),
        tap((store: EStore) => {
          this.dispatchAddProductTempalateToStoreAction(store?.id, item?.id);
        }),
      )
      .subscribe();
  }

  addTemplateToSelectedStore(item: EProductTemplatesListItem) {
    this.selectedStoreId$
      .pipe(
        take(1),
        map((selectedStoreId: string) => {
          this.logProductTemplateUsageInitiatedEvent(EProductTemplateUsageEventStep.ADD_TO_STORE);
          this.dispatchAddProductTempalateToStoreAction(selectedStoreId, item?.id);
        }),
      )
      .subscribe();
  }

  addTemplateVariantToOrder(id: string) {
    this.logProductTemplateUsageInitiatedEvent(EProductTemplateUsageEventStep.PLACE_ORDER);
    this.setLoadingState(true);
    this.eCommerceProductGraphQlService
      .getProduct(id)
      .pipe(
        take(1),
        switchMap(productTemplate => {
          const firstProductVariant = productTemplate?.variants?.[0];

          if (productTemplate?.variants?.length > 1) {
            return this.dialogService
              .open(ProductTemplatesVariantSelectionModalComponent, {
                data: {
                  productTemplate,
                },
              })
              .afterClosed()
              .pipe(
                take(1),
                tap(() => this.setLoadingState(false)),
                filter(result => !!result),
              );
          }

          return of(firstProductVariant);
        }),
        catchError(() => {
          this.setLoadingState(false);
          return of(null);
        }),
      )
      .subscribe((productVariant: EProductVariant) => {
        this.setLoadingState(false);
        this.navigateToEditor(productVariant);
      });
  }

  navigateToEditor(productVariant: EProductVariant) {
    this.router.navigate([
      '/catalogue/print-file-editor',
      {
        designId: productVariant?.designId,
        productUid: productVariant?.productUid,
        pageCount: productVariant?.pageCount || null,
        productName: productVariant?.sanityProductId || null,
        productCategoryName: productVariant?.sanityCategoryId || null,
        from: 'templates',
      },
    ]);
  }

  setSearchParams(payload: EProductTemplatesSearchParams) {
    this.store.dispatch(setSearchParams({ payload }));
  }

  setTemplateName(templateName: string) {
    this.store.dispatch(setTemplateName({ payload: templateName }));
  }

  setSortingParams(sortingParams: EProductTemplatesSearchSortingParams) {
    this.store.dispatch(setSortingParams({ payload: sortingParams }));
  }

  isTemplatesTabEnabledForProductSelection(storeProductMode: StoreProductMode): Observable<boolean> {
    return this.isGelatoUser$.pipe(
      map((isGelatoUser): boolean => {
        if (isGelatoUser) {
          return false;
        }

        return !storeProductMode || storeProductMode === StoreProductMode.PRODUCT_WIZARD;
      }),
    );
  }

  dispatchAddProductTempalateToStoreAction(storeId: string, productTemplateId: string) {
    this.store.dispatch(addProductTemplateToStore({ storeId, productTemplateId }));
  }

  setLoadingState(isLoading: boolean) {
    this.store.dispatch(setLoadingState({ isLoading }));
  }

  private logProductTemplateUsageInitiatedEvent(step: EProductTemplateUsageEventStep) {
    this.store.dispatch(logProductTemplateUsageInitiatedEvent({ step }));
  }
}
